import { createContext, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

export interface ConsentState {
    necessary: true;
    functional: boolean;
    analytics: boolean;
    marketing: boolean;
}

export const initialConsentState: ConsentState = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
};

export interface ConsentContextValue {
    state: ConsentState;
    isReady: boolean;
    openPreferences: () => void;
    setConsent: (next: ConsentState) => void;
    setReady: (ready: boolean) => void;
    registerOpenPreferences: (fn: () => void) => void;
}

export const ConsentContext = createContext<ConsentContextValue | null>(null);

export const ConsentProvider = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState<ConsentState>(initialConsentState);
    const [isReady, setIsReady] = useState(false);
    const openPreferencesRef = useRef<(() => void) | null>(null);

    const setConsent = useCallback((next: ConsentState) => setState(next), []);
    const setReady = useCallback((ready: boolean) => setIsReady(ready), []);
    const registerOpenPreferences = useCallback((fn: () => void) => {
        openPreferencesRef.current = fn;
    }, []);

    const openPreferences = useCallback(() => {
        openPreferencesRef.current?.();
    }, []);

    const value = useMemo<ConsentContextValue>(
        () => ({
            state,
            isReady,
            openPreferences,
            setConsent,
            setReady,
            registerOpenPreferences,
        }),
        [state, isReady, openPreferences, setConsent, setReady, registerOpenPreferences],
    );

    return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};
